import { TrackBuilder, type BuiltPoint } from './builder';

/**
 * スタートから停止まで走るサンプルコースの形（B-55）。どれも駅を出発し、最後の停止ブレーキで止まる。
 * 標準車両（上下 G +5 / −1.5、横 G 1.8）の限界値に収まるよう、半径と緩和曲線を決めている
 * （`project/sample.test.ts` で、走行・評価を確かめる）。
 */

const STANDARD_GRAVITY = 9.80665;

/** チェーンリフト共通：駅 → 30° の斜面を巻き上げ → 頂上で水平に。頂上の高さは `top` 付近。 */
function stationAndLift(top: number, spacing?: number): TrackBuilder {
  const builder = new TrackBuilder([0, 2, 0], spacing)
    .straight(14, { segment: 'station' })
    .pitchTo(20, 30, { segment: 'lift' });
  // 頂上で水平に戻る分（半径 20 m・30° で約 2.7 m）と、入口の円弧の分を除いて、斜面を上がる
  const rise = top - builder.height - 8;
  return builder
    .straight(rise / Math.sin(Math.PI / 6), { segment: 'lift' })
    .pitchTo(20, 0, { segment: 'lift' });
}

/**
 * 傾き −`degrees` の落下を続け、半径 `radius` の引き起こしで高さ `bottom` の水平になるようにする。
 */
function dropTo(
  builder: TrackBuilder,
  bottom: number,
  degrees: number,
  radius: number,
): TrackBuilder {
  // 引き起こしで下がる高さを、同じ命令で測る
  const probe = new TrackBuilder([0, 0, 0], 5, -degrees).pitchTo(radius, 0);
  const pullOut = -probe.height;
  const straight = (builder.height - bottom - pullOut) / Math.sin((degrees * Math.PI) / 180);
  return builder.straight(Math.max(0, straight)).pitchTo(radius, 0);
}

/** ループの進入速度 [m/s]：落下後の実際の速度（標準車両の抵抗込み） */
export const LOOP_ENTRY_SPEED = 22;

/**
 * ローンチ：駅 → 発進加速（静止から 10 m/s² で 30 m/s）→ トップハット（約 39 m）→ キャメルバック → 停止ブレーキ。
 */
export function launchCoasterPoints(): BuiltPoint[] {
  return new TrackBuilder([0, 2, 0])
    .straight(14, { segment: 'station' })
    .straight(60, { segment: 'launch' })
    .straight(10)
    .pitchTo(35, 55) // 引き起こし：30 m/s で約 3.7 G
    .pitchTo(20, -55) // トップハットの頂上
    .pitchTo(35, 0)
    .straight(20)
    .pitchTo(60, 20)
    .pitchTo(45, -20) // キャメルバック：浮遊感（0 G 付近）
    .pitchTo(60, 0)
    .straight(20)
    .straight(60, { segment: 'brake' })
    .build();
}

/**
 * ループ：駅 → チェーンリフト（約 29 m）→ 落下 → 垂直ループ（涙滴形、最下点 3.5 G・頂上 1 G）→ 停止ブレーキ。
 */
export function loopCoasterPoints(v0 = LOOP_ENTRY_SPEED): BuiltPoint[] {
  const builder = stationAndLift(32, 3).straight(4).pitchTo(18, -50);
  return (
    dropTo(builder, 2, 50, 28)
      .straight(15)
      // 進入速度は、落下後の実際の速度（抵抗込みのシミュレーション）に合わせる
      .loop(v0, 3.5, 1, 2)
      .straight(25)
      .straight(60, { segment: 'brake' })
      .build()
  );
}

/**
 * ヘリックス：駅 → チェーンリフト（約 27 m）→ 落下 → バンク付きヘリックス（半径 22 m、下りながら 1.5 周）
 * → 停止ブレーキ。バンク角は、ヘリックスの中ほどの速度で横 G を打ち消す角度。
 */
export function helixCoasterPoints(): BuiltPoint[] {
  const top = 30;
  const radius = 22;
  const turns = 1.5;
  const descent = 2; // 度
  const builder = stationAndLift(top).straight(4).pitchTo(18, -30).pitchTo(25, -descent);
  // ヘリックスの中ほどの速度の見積もり（抵抗で 1 割減）
  const length = 2 * Math.PI * radius * turns;
  const middle = builder.height - (length / 2) * Math.sin((descent * Math.PI) / 180);
  const v2 = 0.9 * 2 * STANDARD_GRAVITY * (top - middle);
  const bank = Math.atan(v2 / (radius * STANDARD_GRAVITY));
  return builder
    .turn(radius, 360 * turns, { bank, transition: 15 })
    .pitchTo(25, 0)
    .straight(15)
    .straight(60, { segment: 'brake' })
    .build();
}

/** 電車・機関車向けの区間のパラメータ：自走する車両の加速（発進加速で表す）と、ゆるやかな制動。 */
const RAIL_BRAKE = { maxDeceleration: 0.9, targetSpeed: 0 } as const;

/**
 * 高原鉄道（蒸気機関車・路面電車向け）：駅を出て、ゆるやかに加速（自走を発進加速で表す。0.8 m/s²、
 * 最高 20 m/s = 72 km/h）→ 小さな起伏 → 半径 300 m の左カーブ（カント 0.05 rad）→ 停車（0.9 m/s²）。
 * 乗り心地は穏やか（横 G 0.15・上下 G 0.9〜1.1 程度）で、電車の限界値（横 0.3 G）に収まる。
 */
export function highlandRailPoints(): BuiltPoint[] {
  return new TrackBuilder([0, 2, 0], 8)
    .straight(30, { segment: 'station' })
    .straight(260, { segment: 'launch', params: { targetSpeed: 20, maxAcceleration: 0.8 } })
    .straight(60)
    .pitchTo(500, 2.5)
    .straight(60)
    .pitchTo(500, 0)
    .straight(60)
    .turn(300, 90, { bank: 0.05, transition: 60 })
    .straight(60)
    .pitchTo(500, -2)
    .straight(40)
    .pitchTo(500, 0)
    .straight(60)
    .straight(330, { segment: 'brake', params: RAIL_BRAKE })
    .build();
}

/**
 * 高速新線（新幹線・リニア向け）：駅を出て、1.5 m/s² で最高 60 m/s（216 km/h）まで加速 → 巡航 →
 * 半径 3,000 m の左カーブ（カント 0.06 rad）→ 1.5 m/s² で停車。全長 約 3.9 km。
 * 横 G は約 0.12 で、新幹線の限界値（0.15 G）に収まる。
 */
export function highSpeedRailPoints(): BuiltPoint[] {
  return new TrackBuilder([0, 2, 0], 20)
    .straight(60, { segment: 'station' })
    .straight(1200, { segment: 'launch', params: { targetSpeed: 60, maxAcceleration: 1.5 } })
    .straight(300)
    .turn(3000, 20, { bank: 0.06, transition: 400 })
    .straight(300)
    .straight(1300, { segment: 'brake', params: { maxDeceleration: 1.5, targetSpeed: 0 } })
    .build();
}

/**
 * ダウンヒル（自転車・スポーツカー向け）：標高 約 55 m の峠から、6〜7 % の下り坂を、
 * 半径 45 m の S 字カーブ（カント付き）で下り、平地でブレーキ（3 m/s²）をかけて止まる。
 * 自転車は、重力と空気抵抗がつり合う速度（約 40 km/h）で下る。スポーツカー（最高 約 90 km/h）も、
 * 平地のブレーキ（130 m・3 m/s²：25 m/s から止まれる）で止まる。
 */
export function downhillPoints(): BuiltPoint[] {
  return new TrackBuilder([0, 60, 0], 4, -3.5)
    .straight(20, { segment: 'station' })
    .straight(80)
    .turn(45, 150, { bank: 0.2, transition: 25 })
    .straight(80)
    .turn(45, -150, { bank: 0.2, transition: 25 })
    .straight(80)
    .turn(45, 150, { bank: 0.2, transition: 25 })
    .straight(80)
    .pitchTo(80, 0)
    .straight(40)
    .straight(130, { segment: 'brake', params: { maxDeceleration: 3, targetSpeed: 0 } })
    .build();
}
